/**
 * @file MediaField.tsx
 * @description Поле картинки вида сайта (phase19.16 Р1; uiux-design-r1 §2,
 * §4, §5, §7): миниатюра или «не задан», «Выбрать из медиатеки» /
 * «Заменить», «Убрать» и подсказка о формате. Выбор — `MediaPickerDialog`.
 */

import { useState } from "react";

import { Button } from "../../ui/button";
import { MediaPickerDialog } from "./MediaPickerDialog";
import type { SiteMedia } from "../../../lib/site-view/types";
import { cn } from "@/lib/utils";

/** Форматы логотипа и значка вкладки (SB77: SVG — позже). */
export const LOGO_MIME = ["image/png", "image/webp"] as const;

interface Props {
  label: string;
  value: SiteMedia | null;
  onChange: (media: SiteMedia | null) => void;
  hint?: string;
  accept?: readonly string[];
  /** Фон миниатюры: логотип для тёмного фона — на тёмном. */
  dark?: boolean;
  /** Форма миниатюры: широкая (обложка, картинка для ссылок) или квадрат. */
  shape?: "wide" | "square";
  disabled?: boolean;
  /** Кадр нового файла (MediaPickerDialog): 1 — фото автора, 1200/630 — картинка для ссылок. */
  cropAspect?: number;
}

export function MediaField({ label, value, onChange, hint, accept, dark = false, shape = "wide", disabled = false, cropAspect }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-on-surface">{label}</span>
      <div
        className={cn(
          "flex items-center justify-center overflow-hidden rounded-xl [box-shadow:inset_0_0_0_1px_var(--border)]",
          shape === "wide" ? "h-28 w-full max-w-72" : "size-24",
          dark ? "bg-[#18181b]" : "bg-surface-low",
        )}
      >
        {value ? (
          <img src={value.url} alt={value.altText ?? label} className="max-h-full max-w-full object-contain p-2" />
        ) : (
          <span className={cn("type-caption", dark ? "text-white/70" : "text-on-surface-variant")}>не задан</span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={disabled} onClick={() => setOpen(true)}>
          {value ? "Заменить" : "Выбрать из медиатеки"}
        </Button>
        {value ? (
          <Button variant="ghost" disabled={disabled} onClick={() => onChange(null)}>
            Убрать
          </Button>
        ) : null}
      </div>
      {hint ? <p className="type-caption text-on-surface-variant">{hint}</p> : null}
      <MediaPickerDialog
        open={open}
        title={label}
        hint={hint}
        accept={accept}
        cropAspect={cropAspect}
        onClose={() => setOpen(false)}
        onSelect={(media) => {
          onChange(media);
          setOpen(false);
        }}
      />
    </div>
  );
}
